import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { API_BASE_URL } from '../../../utils/api';

const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [interests, setInterests] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState(null);

  const interestOptions = [
    { id: 'react', label: 'React & Frontend', icon: 'Code' },
    { id: 'mobile', label: 'Mobile Development', icon: 'Smartphone' },
    { id: 'career', label: 'Career Growth', icon: 'TrendingUp' },
    { id: 'community', label: 'Community Building', icon: 'Users' },
    { id: 'tutorials', label: 'Technical Tutorials', icon: 'BookOpen' },
    { id: 'insights', label: 'Industry Insights', icon: 'Lightbulb' }
  ];

  const benefits = [
    {
      icon: 'Mail',
      title: 'Weekly Insights',
      description: 'Get the latest articles and tutorials delivered to your inbox'
    },
    {
      icon: 'Users',
      title: 'Exclusive Content',
      description: 'Access to subscriber-only resources and early previews'
    },
    {
      icon: 'Zap',
      title: 'Career Tips',
      description: 'Actionable advice for advancing your development career'
    },
    {
      icon: 'Gift',
      title: 'Free Resources',
      description: 'Downloadable guides, templates, and code snippets'
    }
  ];

  const handleInterestToggle = (interestId) => {
    setInterests(prev =>
      prev?.includes(interestId)
        ? prev?.filter(id => id !== interestId)
        : [...prev, interestId]
    );
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email?.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, interests }),
      });

      if (!response.ok) {
        throw new Error('Failed to subscribe. Please try again.');
      }

      setIsSubscribed(true);
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubscribed) {
    return (
      <section className="bg-gradient-brand py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            {/* Success Animation */}
            <div className="relative">
              <div className="w-24 h-24 mx-auto bg-primary/20 rounded-full flex items-center justify-center">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center animate-pulse">
                  <Icon name="Check" size={32} className="text-primary-foreground" />
                </div>
              </div>
              <div className="absolute inset-0 w-24 h-24 mx-auto border-4 border-primary/30 rounded-full animate-ping" />
            </div>

            {/* Success Message */}
            <div className="space-y-4">
              <h3 className="text-3xl font-brand-headline text-white">
                Welcome to the Community! 🎉
              </h3>
              <p className="text-lg text-white/80 font-brand-body max-w-2xl mx-auto">
                Thank you for subscribing! You'll receive your first newsletter within the next few days, packed with exclusive content and insights.
              </p>
            </div>

            {/* Next Steps */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <h4 className="text-xl font-brand-subheading text-white mb-4">
                What's Next?
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="text-center space-y-2">
                  <Icon name="Mail" size={24} className="text-primary mx-auto" />
                  <p className="text-white/80">Check your email for a welcome message</p>
                </div>
                <div className="text-center space-y-2">
                  <Icon name="Users" size={24} className="text-primary mx-auto" />
                  <p className="text-white/80">Join our community</p>
                </div>
                <div className="text-center space-y-2">
                  <Icon name="BookOpen" size={24} className="text-primary mx-auto" />
                  <p className="text-white/80">Explore our latest articles</p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex items-center justify-center space-x-4">
              <a
                href="https://x.com/adrianebesoh237"
                className="p-3 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-brand"
                aria-label="Follow on Twitter"
              >
                <Icon name="Twitter" size={20} />
              </a>
              <a
                href="https://www.linkedin.com/in/ebesoh-adrian"
                className="p-3 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-brand"
                aria-label="Connect on LinkedIn"
              >
                <Icon name="Linkedin" size={20} />
              </a>
              <a
                href="https://whatsapp.com/channel/0029Va8xhP4DZ4LR9n9TA22L"
                className="p-3 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-brand"
                aria-label="Join Channel"
              >
                <Icon name="MessageCircle" size={20} />
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-gradient-brand py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Icon name="Rss" size={24} className="text-primary" />
                <span className="text-sm font-brand-medium text-primary">Newsletter</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-brand-headline text-white leading-tight">
                Stay Ahead with
                <span className="text-gradient-gold block">Developer Insights</span>
              </h2>

              <p className="text-lg text-white/80 font-brand-body">
                Join 5,000+ developers who receive exclusive content, early access to tutorials, and career growth tips directly in their inbox.
              </p>
            </div>

            {/* Benefits Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits?.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-3 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20"
                >
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
                    <Icon name={benefit?.icon} size={16} className="text-primary" />
                  </div>
                  <div className="space-y-1">
                    <h4 className="text-sm font-brand-subheading text-white">
                      {benefit?.title}
                    </h4>
                    <p className="text-xs text-white/70 font-brand-body">
                      {benefit?.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Social Proof */}
            <div className="flex items-center space-x-4 text-sm text-white/60">
              <div className="flex -space-x-2">
                {[...Array(4)]?.map((_, i) => (
                  <div
                    key={i}
                    className="w-8 h-8 bg-primary/20 border-2 border-white/20 rounded-full flex items-center justify-center"
                  >
                    <Icon name="User" size={14} className="text-primary" />
                  </div>
                ))}
              </div>
              <span>Join 5,000+ subscribers</span>
            </div>
          </div>

          {/* Newsletter Form */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Input */}
              <div className="space-y-2">
                <Input
                  type="email"
                  label="Email Address"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  required
                  className="bg-white/20 border-white/30 text-white placeholder-white/50"
                />
              </div>

              {/* Interest Selection */}
              <div className="space-y-3">
                <label className="text-sm font-brand-medium text-white">
                  What interests you most? (Optional)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {interestOptions?.map((option) => (
                    <button
                      key={option?.id}
                      type="button"
                      onClick={() => handleInterestToggle(option?.id)}
                      className={`flex items-center space-x-2 p-3 rounded-lg text-sm font-brand-medium transition-brand ${
                        interests?.includes(option?.id)
                          ? 'bg-primary/20 text-primary border border-primary/30' :'bg-white/10 text-white/80 border border-white/20 hover:bg-white/20'
                      }`}
                    >
                      <Icon name={option?.icon} size={14} />
                      <span className="text-xs">{option?.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="default"
                fullWidth
                loading={isSubmitting}
                disabled={!email?.trim() || isSubmitting}
                iconName="Send"
                iconPosition="right"
                iconSize={16}
                className="bg-primary text-primary-foreground hover:bg-primary/90 brand-shadow"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe Now'}
              </Button>

              {/* Error Message */}
              {error && (
                <div className="text-sm text-red-400 text-center">
                  {error}
                </div>
              )}

              {/* Privacy Note */}
              <p className="text-xs text-white/60 text-center">
                No spam, ever. Unsubscribe anytime with one click.
                <br />
                <a href="#" className="text-primary hover:text-primary/80 transition-brand">
                  Privacy Policy
                </a>
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSignup;